/**
 * @packageDocumentation
 * @module      CorPageSystems.ts
 * @description The Systems page for this application.
 * @copyright   Copyright (c) 2021 by Corindus Inc., a Siemens Healthineers Company. All rights reserved.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var CorPageSystemsCard_1;
import { css, customElement, html, internalProperty } from 'lit-element';
import CloudApi from "./CloudApi";
import Fetcher from "./Fetcher";
import SystemEx from "./SystemEx";
import UserDetails from "./UserDetails";
import SystemState from "./SystemState";
import { CardStatus, CorPageEntityMaintenance, CorPageEntityMaintenanceCard, CorPageEntityMaintenanceList, ItemAddedOrUpdatedEvent, ItemChosenEvent, ShowPopoverEvent } from "./CorPageEntityMaintenance";
import SystemExDto from "./SystemExDto";
import AssociationState from "./AssociationState";
let corPageSystemsFetcher = new Fetcher();
/** Object through which all API access occurs. In unit tests, overwrite this with a mock. */
export { corPageSystemsFetcher };
var Tag;
(function (Tag) {
    Tag["Available"] = "AVAILABLE";
    Tag["Associated"] = "ASSOCIATED";
    Tag["Pending"] = "ASSOCIATION_PENDING";
    Tag["Rejected"] = "ASSOCIATION_REJECTED";
    Tag["TimedOut"] = "ASSOCIATION_REQUEST_TIMED_OUT";
    Tag["ControlSide"] = "Control-side";
    Tag["PatientSide"] = "Patient-side";
})(Tag || (Tag = {}));
/** The Systems page, comprised of a list on the left and a card for the selected system on the right. */
let CorPageSystems = class CorPageSystems extends CorPageEntityMaintenance {
    get selectorForList() {
        return 'cor-page-systems-list';
    }
    get selectorForCard() {
        return 'cor-page-systems-card';
    }
    get blankCardImageFile() {
        return 'system.svg';
    }
    get singleItemName() {
        return 'system';
    }
    render() {
        let cardContents = html `
            ${this.cardStatus == CardStatus.Hidden ?
            this.blankCardHtml :
            html `
                        <cor-page-systems-card id="card"
                                               itemid="${this.itemId}"
                        ></cor-page-systems-card>`}`;
        return html `
            <sh-page theme="${this.currentTheme}">
                ${this.popoverHtml}
                <sh-card slot="left" id="list">
                    <cor-page-systems-list
                            activeitemid="${this.itemId}">
                    </cor-page-systems-list>
                    <div slot="footer">
                        <sh-button label='Refresh'
                                   ?disabled="${!this.okToRefresh}"
                                   color="secondary"
                                   @click=${() => this.refreshList()}>
                        </sh-button>
                        <sh-button label='Add'
                                   ?disabled="${!this.okToAdd}"
                                   @click=${() => this.addItem()}>
                        </sh-button>
                    </div>
                </sh-card>
                ${cardContents}
            </sh-page>`;
    }
};
CorPageSystems = __decorate([
    customElement('cor-page-systems')
], CorPageSystems);
export { CorPageSystems };
/**
 * The list of systems on the Systems page.
 *
 * Designed to be a child of cor-page-systems; only exported for the sake of unit testing.
 */
let CorPageSystemsList = class CorPageSystemsList extends CorPageEntityMaintenanceList {
    constructor() {
        super(...arguments);
        this.clearFilter = () => {
            this.tags = [];
            this.tagComponent = [];
        };
        this.tagComponentBuilder = (tag) => {
            return html `<sh-tag class="margin-1" label="${this.getDisplayName(tag)}" @click=${() => this.tagSelector(tag)} removable></sh-tag>`;
        };
        this.toggleTag = (tag) => {
            if (this.tags.includes(tag))
                this.tags = this.tags.filter(x => x !== tag);
            else
                this.tags.push(tag);
            return this.tags;
        };
        this.tagSelector = (tag) => {
            this.currentPage = 1;
            this.tags = this.toggleTag(tag);
            this.tags = [...this.tags];
            this.tagComponent = [];
            for (let i = 0; i < this.numberOftags && i < this.tags.length; i++)
                this.tagComponent.push(this.tagComponentBuilder(this.tags[i]));
            if (this.numberOftags < this.tags.length)
                this.tagComponent.push(html `<sh-tag class="margin-1" label="${this.tags.length - this.numberOftags} more filters"></sh-tag>`);
        };
        this.tags = [];
        this.tagComponent = [];
        this.isShowPopover = false;
        this.numberOftags = 7;
    }
    static get styles() {
        return css `
            ${CorPageEntityMaintenanceList.styles}
            .filterbadge {
                position: absolute;
                top: 0px;
                right: 1px;
            }

            .corPageList {
                display: flex;
                position: relative;
            }

            .listLength {
                display: inline;
                width: 15%;
            }

            .tagComponent {
                width: 80%;
                display: flex;
                flex-wrap: wrap
            }

            .filter {
                top: 5px;
                cursor: pointer;
            }

            .clearAll {
                position: absolute;
                right: -75%;
                top: 20px;
                cursor: pointer
            }

            .tagGroup {
                display: flex;
                flex-wrap: wrap;
            }

            .margin-1 {
                margin: 1px;
            }

            .margin-4 {
                margin-bottom: .25rem;
            }
        `;
    }
    summarySortCompare(a, b) {
        return a.systemName.localeCompare(b.systemName);
    }
    getSummaries() {
        return new CloudApi(corPageSystemsFetcher, this).getSystems();
    }
    getItemIdAsStringFromSummary(summary) {
        return summary.systemId;
    }
    calculateFilteredPages() {
        return (Math.ceil(this.filteredItemsList.length / this.itemsPerPage));
    }
    getBadge() {
        return html `
            <sh-badge
                class="filterbadge"
                style="display:${this.tags.length > 0 ? "block" : "none"};"
                label="${this.tags.length}"
            ></sh-badge>
            `;
    }
    tagStatus(tag) {
        return this.tags.includes(tag) ? "#eee" : "";
    }
    getDisplayName(tag) {
        switch (tag) {
            case 'AVAILABLE':
                return "Available";
            case 'ASSOCIATED':
                return "Associated";
            case 'ASSOCIATION_PENDING':
                return "Pending";
            case 'ASSOCIATION_REJECTED':
                return "Rejected";
            case 'ASSOCIATION_REQUEST_TIMED_OUT':
                return "Timed Out";
            case 'Control-side':
                return "Control-side";
            case 'Patient-side':
                return "Patient-side";
            default:
                return tag;
        }
    }
    get Tags() {
        return this.tags;
    }
    get TagComponent() {
        return this.tagComponent;
    }
    get FilteredItemsList() {
        return this.filteredItemsList;
    }
    /** Renders the component as part of LitElement's lifecycle. */
    render() {
        this.filteredItemsList = this.itemsList.filter(x => this.tags.length
            ? (this.tags.includes(x.systemState) || this.tags.includes(x.systemSideString))
            : true);
        this.Pages = this.calculateFilteredPages();
        return html `
            <div class="corPageList" id="filter">
                <div class="listLength">
                    <sh-text class="paddingAll" size="body-2">${this.filteredItemsList.length} systems </sh-text>
                </div>
                <div class="tagComponent">
                    ${this.tagComponent}
                </div>
                <div>
                <sh-icon
                    icon="filter"
                    class="filter"
                    @click=${() => this.isShowPopover = !this.isShowPopover}
                    id="code-sandbox-icon">
                </sh-icon>
                ${this.getBadge()}
                   
                <sh-popover
                    label="Filter"
                    sticky
                    ?visible=${this.isShowPopover}
                    passive
                    position="bottom-right"
                    target="code-sandbox-icon">

                    <sh-text class="clearAll" @click=${() => this.clearFilter()}><u>Clear all</u></sh-text>

                    <sh-text class="margin-4" size='title-1'>Status</sh-text>
                    <div class="tagGroup">
                        <sh-tag class="margin-1" style="background-color:${this.tagStatus(Tag.Available)};" label="${this.getDisplayName(Tag.Available)}" @click=${() => this.tagSelector(Tag.Available)}></sh-tag>
                        <sh-tag class="margin-1" style="background-color:${this.tagStatus(Tag.Associated)};" label="${this.getDisplayName(Tag.Associated)}" @click=${() => this.tagSelector(Tag.Associated)}></sh-tag>
                        <sh-tag class="margin-1" style="background-color:${this.tagStatus(Tag.Pending)};" label="${this.getDisplayName(Tag.Pending)}" @click=${() => this.tagSelector(Tag.Pending)}></sh-tag>
                        <sh-tag class="margin-1" style="background-color:${this.tagStatus(Tag.Rejected)};" label="${this.getDisplayName(Tag.Rejected)}" @click=${() => this.tagSelector(Tag.Rejected)} ></sh-tag>
                        <sh-tag class="margin-1" style="background-color:${this.tagStatus(Tag.TimedOut)};" label="${this.getDisplayName(Tag.TimedOut)}" @click=${() => this.tagSelector(Tag.TimedOut)}></sh-tag>
                    </div>
                    <br>

                    <sh-text class="margin-4" size='title-1'>System Type</sh-text>
                    <div class="tagGroup">
                        <sh-tag class="margin-1" style="background-color:${this.tagStatus(Tag.ControlSide)};" label="${Tag.ControlSide}" @click=${() => this.tagSelector(Tag.ControlSide)}></sh-tag>
                        <sh-tag class="margin-1" style="background-color:${this.tagStatus(Tag.PatientSide)};" label=${Tag.PatientSide} @click=${() => this.tagSelector(Tag.PatientSide)}></sh-tag>
                    </div>
                    <br>
                </sh-popover>

                </div> 
            </div>
            
            
            <sh-table ?readonly=${!this.okToSelectNewItemInList} id="systems-table">
                <sh-table-row slot="header">
                    <sh-table-head unsortable columns="12">Name</sh-table-head>
                    <sh-table-head unsortable columns="12">Health System</sh-table-head>
                    <sh-table-head unsortable columns="12">Type</sh-table-head>
                    <sh-table-head unsortable columns="12">Status</sh-table-head>
                </sh-table-row>
                ${this.filteredItemsList.map((system, index) => this.isWithinPageRange(index) ? html `
                    <sh-table-row
                            id="${this.databaseIdToHtmlId.get(this.getItemIdAsStringFromSummary(system))}"
                            columns="48"
                            ?active=${this.activeItemId === system.systemId}
                            ?lastAddedOrUpdated=${this.lastAddedOrUpdatedId === system.systemId}
                            @click=${() => this.dispatchEvent(new ItemChosenEvent(system.systemId))}>
                        <sh-table-cell columns="12" title="${system.systemName}">
                            <sh-text class="list-text-wrapper"> ${system.systemName} </sh-text>
                        </sh-table-cell>
                        <sh-table-cell columns="12" title="${system.hospitalName}">
                            <sh-text class="list-text-wrapper"> ${system.hospitalName} </sh-text>
                        </sh-table-cell>
                        <sh-table-cell columns="12" title="${system.systemSideString}">
                            <sh-text class="list-text-wrapper"> ${system.systemSideString} </sh-text>
                        </sh-table-cell>
                        <sh-table-cell columns="12" title="${SystemState.getFriendlyName(system.systemState)}">
                            <sh-text class="list-text-wrapper"> <cor-system-state state="${system.systemState}"></cor-system-state> </sh-text>
                        </sh-table-cell>
                    </sh-table-row>` : html ``)}
            </sh-table>
			<sh-pagination pages="${this.Pages}" current-page="${this.currentPage}" @click="${this.changeCurrentPage}"> </sh-pagination>
        `;
    }
};
__decorate([
    internalProperty()
], CorPageSystemsList.prototype, "tags", void 0);
CorPageSystemsList = __decorate([
    customElement('cor-page-systems-list')
], CorPageSystemsList);
export { CorPageSystemsList };
/**
 * A system that is being added or updated on the Systems page.
 *
 * Designed to be a child of cor-page-systems; only exported for the sake of unit testing.
 */
let CorPageSystemsCard = CorPageSystemsCard_1 = class CorPageSystemsCard extends CorPageEntityMaintenanceCard {
    constructor() {
        super(...arguments);
        this.status = '';
        this.controlSide = 'Control-Side';
        this.patientSide = 'Patient-Side';
        this.associatedSystem = undefined;
        this.roboticNetworkSummaries = [];
        this.associatedRoboticNetworkSummaries = [];
        this.showAssociatedSystem = false;
        this.corPageSystemsList = new CorPageSystemsList();
        this.generatingNewPassword = false;
    }
    static get styles() {
        return css `
          ${CorPageSystemsCard_1.entityMaintenanceCardStyles}
          #Outer {
            display: flex;
            justify-content: space-around;
          }

          #Outer > div {
            height: 100%;
          }

          #SystemLeftButtons sh-button {
            margin-top: 24px;
          }
          
          div.system-title-area {
            height: 56px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          #ThisSystem.no-associated {
            width: 100%;
          }

          #NewPasswordModalContent {
            display: flex;
            flex-direction: column;
            justify-content: space-around;
            height: 90%;
          }

          #ThisSystem.showing-associated, #AssociatedSystem {
            width: 48%;
          }

          #InfoIconAndText {
            display: flex;
            flex-direction: row;
            align-items: center;
          }
          
          .no-display {
            display: none;
          }

          div.system-information, div.system-status {
            display: grid;
            grid-template-rows: 50px;
            grid-row-gap: 24px;
            grid-column-gap: 24px;
            justify-content: start;
            align-content: start;
            align-items: baseline;
            margin-bottom: 24px;
          }

          .system-name {
            grid-area: name;
          }

          .system-id {
            grid-area: id;
          }

          .system-type {
            grid-area: type;
          }

          .system-enabled {
            grid-area: enabled;
            height: 1.5rem; // Causes label to align with 1.5rem-height checkbox.
          }

          .system-status-summary {
            grid-area: summary;
            display: flex;
            align-items: baseline;
          }
          
          .system-status-summary > sh-icon {
            margin-left: -20px;
          }

          .association {
            grid-area: association;
          }

          .system-last-update {
            grid-area: update;
          }

          .system-online {
            grid-area: online;
          }

          .system-created {
            grid-area: created;
          }

          .system-health-system {
            grid-area: health;
          }

          .system-address {
            grid-area: address;
          }

          .system-version {
            grid-area: version;
          }

          div.system-information.no-associated {
            width: 75%;
            grid-row-gap: 24px;
            grid-column-gap: 24px;
            grid-template-columns: 1fr 1fr 1fr;
            grid-template-areas:
              'name   id      type   '
              'health address address'
              'created version .';
          }

          div.system-information.showing-associated {
            width: 100%;
            grid-row-gap: 24px;
            grid-column-gap: 24px;
            grid-template-columns: 1fr 1fr;
            grid-template-areas:
              'name    id     '
              'type    health'
              'address address'
              'created version';
          }

          div.system-status.no-associated {
            width: 75%;
            grid-row-gap: 24px;
            grid-column-gap: 24px;
            grid-template-columns: 1fr 1fr 1fr;
            grid-template-areas:
              'summary  association  enabled'
              'update   online       .';
          }

          div.system-status.showing-associated {
            width: 100%;
            grid-row-gap: 24px;
            grid-column-gap: 24px;
            grid-template-columns: 1fr 1fr 1fr;
            grid-template-areas:              
              'summary  association  enabled'
              'update   update       .'
              'online   online       .';
          }

          .device-type {
            grid-area: type;
          }

          .device-status {
            grid-area: status;
          }

          .device-version {
            grid-area: version;
          }

          .device-last-update {
            grid-area: update;
          }

          div.devices {
            width: 100%;
            display: grid;
            grid-template-rows: 50px;
            margin-bottom: 24px;
          }

          div.no-associated div.devices {
            grid-row-gap: 24px;
            grid-column-gap: 24px;
          }

          div.showing-associated div.devices {
            grid-row-gap: 24px;
            grid-column-gap: 24px;
          }

          div.device {
            width: 100%;
            display: grid;
            grid-row-gap: 24px;
            grid-column-gap: 24px;
            justify-content: start;
            align-content: start;
            grid-template-areas: 'type status version update';
          }

          div.no-associated div.device {
            grid-template-columns: 1fr 1fr 1fr 1fr;
          }

          div.showing-associated div.device {
            grid-template-columns: 1fr 1fr 1fr 2fr;
          }
        `;
    }
    constructItem() {
        this.associatedSystem = undefined;
        let systemEx = new SystemEx();
        systemEx.system.isControlSide = true; // So we get some devices.
        return systemEx;
    }
    getItem(systemId) {
        const cloudApi = new CloudApi(corPageSystemsFetcher, this);
        const self = this;
        this.showAssociatedSystem = false;
        this.associatedSystem = undefined;
        this.roboticNetworkSummaries.length = 0;
        this.associatedRoboticNetworkSummaries.length = 0;
        // Just in case the user clicked away from the New Password modal instead of pressing a button
        this.generatingNewPassword = false;
        // The following fetches are intentionally not chained. This method will return the Promise from the
        // only one that urgently matters. As data for the others arrive, the page will adjust.
        const promiseForSystem = new CloudApi(corPageSystemsFetcher, this).getSystemEx(systemId);
        cloudApi.getRoboticNetworkSummariesForSystem(systemId).then((rnSummaries) => {
            self.roboticNetworkSummaries = rnSummaries;
        });
        cloudApi.getAssociatedSystem(systemId).then((assoc) => {
            self.associatedSystem = assoc;
            if (assoc !== undefined) {
                cloudApi.getRoboticNetworkSummariesForSystem(assoc.systemId).then((assocRnSummaries) => {
                    self.associatedRoboticNetworkSummaries = assocRnSummaries;
                });
            }
        });
        return promiseForSystem;
    }
    deleteItem(item) {
        return new CloudApi(corPageSystemsFetcher, this).deleteSystem(item.system.systemId);
    }
    get genericItemName() {
        return "system";
    }
    get currentItemName() {
        return this.workItem.system.systemName;
    }
    get systemName() {
        return this.workItem.system.systemName || '';
    }
    set systemName(newValue) {
        this.setterBody(newValue, 'systemName', this.workItem.system);
    }
    get systemIdInternal() {
        return this.workItem.system.systemId || '';
    }
    set systemIdInternal(newValue) {
        this.setterBody(newValue, 'systemIdInternal', this.workItem.system, 'systemId');
    }
    get systemState() {
        return this.workItem.system.systemState || '';
    }
    set systemState(newValue) {
        this.setterBody(newValue, 'systemState', this.workItem.system);
    }
    get associationState() {
        return this.workItem.system.associationState || '';
    }
    get systemCreated() {
        return this.workItem.system.created || '';
    }
    get systemSide() {
        var _a;
        if (!((_a = this.workItem) === null || _a === void 0 ? void 0 : _a.system)) {
            return '';
        }
        return this.workItem.system.isPatientSide ? this.patientSide : this.controlSide;
    }
    set systemSide(newValue) {
        const oldValue = this.systemSide;
        if (oldValue !== newValue) {
            this.workItem.system.isControlSide = newValue === this.controlSide;
        }
        this.requestUpdate('systemSide', oldValue);
    }
    get hospitalName() {
        return this.workItem.system.hospitalName || '';
    }
    set hospitalName(newValue) {
        this.setterBody(newValue, 'hospitalName', this.workItem.system);
    }
    get address() {
        return this.workItem.system.address || '';
    }
    set address(newValue) {
        this.setterBody(newValue, 'address', this.workItem.system);
    }
    get version() {
        return this.workItem.system.version || '';
    }
    get systemIsEnabled() {
        return this.workItem.system.isEnabled;
    }
    set systemIsEnabled(newValue) {
        this.workItem.system.enable(newValue);
        // This looks awful but without both the requestUpdate and the setTimeout we don't get
        // both the toggle and the systemState to update properly.
        setTimeout(() => {
            this.requestUpdate('systemState');
        }, 0);
    }
    /** Override the base class's method that determines the contents of the Delete System modal. */
    getDeleteBody() {
        return html `
            <sh-text>System Name: ${this.systemName}</sh-text>
            <sh-text>System ID: ${this.systemIdInternal}</sh-text>
            <sh-text>Health System: ${this.hospitalName}</sh-text>
        `;
    }
    toggleSystemEnabled() {
        this.systemIsEnabled = !this.systemIsEnabled;
        this.validationTracker.nonMandatoryString("systemEnabled", () => { return this.workItem.system.enabled; }, () => { return this.workItemPrevious.system.enabled; });
    }
    get isSystemAssociated() {
        return !!this.associatedSystem;
    }
    toggleShowAssociatedSystem() {
        this.showAssociatedSystem = !this.showAssociatedSystem;
    }
    initiateEdit() {
        this.validationTracker.resetPreviousValidationResults();
        this.workItemPrevious = JSON.parse(JSON.stringify(this.workItem));
        this.showAssociatedSystem = false;
        this.changeCardStatus(CardStatus.Editing);
    }
    goToAssociated() {
        this.dispatchEvent(new ItemChosenEvent(this.associatedSystem.systemId));
    }
    get systemsLastReport() {
        return this.getLastReport(this.workItem.system);
    }
    get associatedSystemsLastReport() {
        return this.getLastReport(this.associatedSystem);
    }
    getLastReport(system) {
        if (system === undefined) { // Should never happen
            return '';
        }
        if (!system.lastHeard) {
            return '(No report yet.)';
        }
        return `${system.lastHeard.toLocaleString()}: ${system.fit ? 'Fit for use.' : 'Unfit for use.'}`;
    }
    get systemsOnlineStatus() {
        return this.getOnlineStatus(this.workItem.system);
    }
    get associatedSystemsOnlineStatus() {
        return this.getOnlineStatus(this.associatedSystem);
    }
    getOnlineStatus(system) {
        if (system === undefined) { // Should never happen
            return '';
        }
        if (system.missingSince) {
            return `${system.missingSince.toLocaleString()}: Missing`;
        }
        if (system.lastHeard) {
            return `${system.lastHeard.toLocaleString()}: Online`;
        }
        return '(No report yet.)';
    }
    initiateGenerateNewPassword() {
        this.generatingNewPassword = true;
    }
    generateNewPassword() {
        this.generatingNewPassword = false;
        this.save();
    }
    cancelGenerateNewPassword() {
        this.generatingNewPassword = false;
    }
    downloadConfig() {
        var _a;
        const textLines = [
            '[server]',
            'host=' + appConfig.sessionManagerHost,
            'port=' + appConfig.sessionManagerPort,
            'scheme=' + appConfig.sessionManagerScheme,
            '',
            '; Cloud API credentials',
            '[user]',
            'password="' + ((_a = this.workItem.user) === null || _a === void 0 ? void 0 : _a.password) + '"',
            '',
            '; local system info',
            '[system]',
            'systemId=' + this.systemIdInternal,
            'simulation=0',
            'cache=1',
        ];
        const iniContents = textLines.join('\r\n');
        let element = document.createElement('a');
        element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(iniContents));
        element.setAttribute('download', 'cloud.ini');
        element.style.display = 'none';
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
    }
    save() {
        const that = this;
        if (!that.workItem.user) {
            that.workItem.user = new UserDetails();
        }
        that.workItem.user.username = this.systemIdInternal;
        const api = new CloudApi(corPageSystemsFetcher, this);
        // If we're editing, save without generating a new password.
        // Otherwise (adding or viewing), generate one. The 'viewing' case is when the user has
        // requested a new password and config file.
        let passwordPromise = this.editing ?
            Promise.resolve(null) : // null means don't change password.
            api.generateNewPassword(this);
        passwordPromise.then((newPwd) => {
            that.workItem.user.password = newPwd;
            const dto = new SystemExDto(this.workItem);
            let promise = that.cardStatus == CardStatus.Adding ?
                api.createSystemEx(dto) :
                api.updateSystemEx(dto);
            promise.then((response) => {
                if (response === null || response === void 0 ? void 0 : response.ok) {
                    that.itemId = that.systemIdInternal;
                    if (newPwd != null) {
                        that.downloadConfig();
                        that.dispatchEvent(new ShowPopoverEvent("Configuration File", `A new configuration file for ${that.systemName} is downloaded to your system.`));
                    }
                    else if (that.editing) {
                        that.dispatchEvent(new ShowPopoverEvent("System Saved", `System ${that.systemName} is updated.`));
                    }
                    that.dispatchEvent(new ItemAddedOrUpdatedEvent(that.itemId, that.adding));
                    that.changeCardStatus(CardStatus.Viewing);
                }
            });
        });
    }
    render() {
        var _a, _b, _c;
        const self = this;
        return html `
            <sh-card id="card" label="${this.adding ? 'Add New System' : this.workItem.system.systemName}">
                <div id="Outer">
                    <div id="ThisSystem" class="${this.showAssociatedSystem ? 'showing-associated' : 'no-associated'}">
                        <div class="system-title-area paddingTop8pixel paddingBottom24Pixel">
                            <sh-text size="title-1">
                                ${this.adding ? 'Complete System Information' : 'System Information'}
                            </sh-text>
                        </div>
                        <div class="system-information ${this.showAssociatedSystem ? 'showing-associated' : 'no-associated'}">
                            <sh-input-text label="System Name"
                                           class="system-name"
                                           ?readonly=${self.viewing}
                                           ?mandatory="${self.validationTracker.alwaysMandatoryString('systemName', () => self.systemName, () => this.workItemPrevious.system.systemName)}"
                                           value="${self.systemName}"
                                           @input=${self.inputHelper('systemName')}>
                            </sh-input-text>
                            <sh-input-text label="System ID"
                                           class="system-id"
                                           ?readonly=${self.viewing || this.editing}
                                           value="${self.systemIdInternal}"
                                           ?mandatory="${self.validationTracker.alwaysMandatoryString('systemId', () => self.systemIdInternal, () => this.workItemPrevious.system.systemId)}"
                                           ?error = "${this.validationTracker.error("systemId", () => {
            return !((this.hasFieldValueChanged(this, "systemId")) &&
                (this.corPageSystemsList.isItemAvailable(self.systemIdInternal, "systemId")));
        })}"
                                           error-message = "This System Id is already present."
                                           @input=${self.inputHelper('systemIdInternal')}>
                            </sh-input-text>
                            <sh-dropdown label="System Type"
                                         class="system-type"
                                         ?readonly=${!self.adding}
                                         value="${self.systemSide}"
                                         @input=${self.inputHelper('systemSide')}>
                                <sh-menu-item label="${self.controlSide}"
                                              ?active=${this.systemSide == this.controlSide}
                                              @clicked=${() => this.systemSide = this.controlSide}>
                                </sh-menu-item>
                                <sh-menu-item label="${self.patientSide}"
                                              ?active=${this.systemSide == this.patientSide}
                                              @clicked=${() => this.systemSide = this.patientSide}>
                                </sh-menu-item>
                            </sh-dropdown>
                            <sh-input-text label="Health System"
                                           class="system-health-system"
                                           value="${self.hospitalName}"
                                           ?readonly=${self.viewing}
                                           ?mandatory="${self.validationTracker.alwaysMandatoryString('hospitalName', () => this.hospitalName, () => this.workItemPrevious.system.hospitalName)}"
                                           @input=${self.inputHelper('hospitalName')}>
                            </sh-input-text>
                            <sh-input-text label="Address"
                                           class="system-address"
                                           value="${self.address}"
                                           ?readonly=${self.viewing}
                                           ?mandatory="${self.validationTracker.alwaysMandatoryString('address', () => self.address, () => this.workItemPrevious.system.address)}"
                                           @input=${self.inputHelper('address')}>
                            </sh-input-text>

                            ${!this.adding ? html `
                                <sh-input-text label="Created"
                                               class="system-created"
                                               readonly
                                               value="${(_a = this.systemCreated) === null || _a === void 0 ? void 0 : _a.toLocaleString()}">
                                </sh-input-text>
                                <sh-input-text label="Version"
                                               class="system-version"
                                               readonly
                                               value="${self.version}">
                                </sh-input-text>` : ''}
                        </div>
                        ${!this.adding ? html `
                            <div class="system-title-area paddingBottom24Pixel">
                                <sh-text size="title-1">
                                    System Status
                                </sh-text>
                            </div>
                            <div class="system-status
                                 ${this.showAssociatedSystem ? 'showing-associated' : 'no-associated'}">
                                <div class="system-status-summary">
                                    <sh-input-text
                                            label="Status Summary"
                                            value="${SystemState.getFriendlyName(this.systemState)}"
                                            readonly>
                                    </sh-input-text>
                                    <sh-icon icon="circle-filled"
                                             size="xs"
                                             color="${SystemState.getIconColor(this.systemState)}"
                                    ></sh-icon>
                                </div>
                                <sh-input-text
                                        label="Association Status"
                                        class="association"
                                        value="${AssociationState.getFriendlyName(this.associationState)}"
                                        readonly>
                                </sh-input-text>
                                <sh-checkbox label="Enabled"
                                           class="system-enabled"
                                           ?disabled="${!this.editing}"
                                           @click="${self.toggleSystemEnabled}"
                                           ?active="${self.systemIsEnabled}">
                                </sh-checkbox>
                                <sh-input-text label="System Controller's Last Report"
                                               class="system-last-update"
                                               readonly
                                               value="${this.systemsLastReport}">
                                </sh-input-text>
                                <sh-input-text label="Online Status"
                                               class="system-online"
                                               readonly
                                               value="${this.systemsOnlineStatus}">
                                </sh-input-text>
                            </div>
                        ` : html ``}
                        ${!this.adding ? html `
                            <div class="system-title-area paddingBottom24Pixel">
                                <sh-text size="title-1">Device Information</sh-text>
                            </div>
                            <div class="devices">
                                ${this.workItem.system.devices.map((device) => {
            var _a;
            return html `
                                    <div class="device">
                                        <sh-input-text label="Device Type"
                                                       class="device-type"
                                                       value="${device.deviceType}"
                                                       readonly>
                                        </sh-input-text>
                                        <sh-input-text label="Version"
                                                       class="device-version"
                                                       value="${device.softwareVersion || ''}"
                                                       readonly>
                                        </sh-input-text>
                                        <sh-input-text label="Device Status"
                                                       class="device-status"
                                                       readonly
                                                       value="${device.deviceState || ''}">
                                        </sh-input-text>
                                        <sh-input-text label="Last Status Update"
                                                       class="device-last-update"
                                                       readonly
                                                       value="${((_a = device.lastHeard) === null || _a === void 0 ? void 0 : _a.toLocaleString()) || '(never)'}">
                                        </sh-input-text>
                                    </div>
                                `;
        })}
                            </div>
                            <div class="system-title-area paddingBottom24Pixel">
                                <sh-text size="title-1">Robotic Networks</sh-text>
                            </div>
                            <div class="robotic-networks">
                                ${this.roboticNetworkSummaries.length > 0 ?
            (_b = this.roboticNetworkSummaries) === null || _b === void 0 ? void 0 : _b.map((rnSummary) => html `
                                    <div>
                                    <sh-text size="body-1">${rnSummary.roboticNetworkName}</sh-text>
                                    </div>`) :
            html `<sh-text size="body-1">(none)</sh-text>`}
                            </div>
                        ` : html ``}
                    </div>
                    ${this.viewing && this.showAssociatedSystem ? html `
                        <div id="SystemsDivider">
                            <sh-divider vertical></sh-divider>
                        </div>
                        <div id="AssociatedSystem" class="showing-associated">
                            <div class="system-title-area paddingBottom24Pixel">
                                <sh-text size="title-1">Associated System
                                </sh-text>
                                <sh-button label='System Profile'
                                           size="l"
                                           @click=${self.goToAssociated} color="tertiary">
                                </sh-button>
                            </div>
                            <div class="system-information showing-associated">
                                <sh-input-text label="System Name"
                                               class="system-name"
                                               readonly
                                               value="${self.associatedSystem.systemName}">
                                </sh-input-text>
                                <sh-input-text label="System ID"
                                               class="system-id"
                                               readonly
                                               value="${self.associatedSystem.systemId}" }>
                                </sh-input-text>
                                <sh-input-text label="System Type"
                                               class="system-type"
                                               readonly
                                               value="${self.associatedSystem.isPatientSide ?
            self.patientSide : self.controlSide}">
                                </sh-input-text>
                                <sh-input-text label="Health System"
                                               class="system-health-system"
                                               value="${this.associatedSystem.hospitalName}"
                                               readonly>
                                </sh-input-text>
                                <sh-input-text label="Address"
                                               class="system-address"
                                               value="${this.associatedSystem.address}"
                                               readonly>
                                </sh-input-text>
                                <sh-input-text label="Created"
                                               class="system-created"
                                               readonly
                                               value="${this.associatedSystem.created.toLocaleString()}">
                                </sh-input-text>
                                <sh-input-text label="Version"
                                               class="system-version"
                                               readonly
                                               value="${this.associatedSystem.version}">
                                </sh-input-text>
                            </div>
                            <div class="system-title-area paddingBottom24Pixel">
                                <sh-text size="title-1">
                                    System Status
                                </sh-text>
                            </div>
                            <div class="system-status showing-associated">
                                <div class="system-status-summary">
                                    <sh-input-text
                                            label="Status Summary"
                                            value="${SystemState.getFriendlyName(this.associatedSystem.systemState)}"
                                            readonly>
                                    </sh-input-text>
                                    <sh-icon icon="circle-filled"
                                             size="xs"
                                             color="${SystemState.getIconColor(this.associatedSystem.systemState)}"
                                    ></sh-icon>
                                </div>                                
                                <sh-input-text
                                        label="Association Status"
                                        class="association"
                                        value="${AssociationState.getFriendlyName(this.associatedSystem.associationState)}"
                                        readonly>
                                </sh-input-text>
                                <sh-checkbox label="Enabled"
                                           class="system-enabled"
                                           ?disabled="${!this.editing}"
                                           @click="${self.toggleSystemEnabled}"
                                           ?active="${self.systemIsEnabled}">
                                </sh-checkbox>
                                <sh-input-text label="System Controller's Last Report"
                                               class="system-last-update"
                                               readonly
                                               value="${this.associatedSystemsLastReport}">
                                </sh-input-text>
                                <sh-input-text label="Online Status"
                                               class="system-online"
                                               readonly
                                               value="${this.associatedSystemsOnlineStatus}">
                                </sh-input-text>
                            </div>
                            <div class="system-title-area paddingBottom24Pixel">
                                <sh-text size="title-1">Device Information</sh-text>
                            </div>
                            <div class="devices">
                                ${this.associatedSystem.devices.map((device) => {
            var _a;
            return html `
                                    <div class="device">
                                        <sh-input-text label="Device Type"
                                                       class="device-type"
                                                       value="${device.deviceType}"
                                                       readonly>
                                        </sh-input-text>
                                        <sh-input-text label="Version"
                                                       class="device-version"
                                                       value="${device.softwareVersion || ''}"
                                                       readonly>
                                        </sh-input-text>
                                        <sh-input-text label="Device Status"
                                                       class="device-status"
                                                       readonly
                                                       value="${device.deviceState || ''}">
                                        </sh-input-text>
                                        <sh-input-text label="Last Status Update"
                                                       class="device-last-update"
                                                       readonly
                                                       value="${((_a = device.lastHeard) === null || _a === void 0 ? void 0 : _a.toLocaleString()) || '(never)'}">
                                        </sh-input-text>
                                    </div>
                                `;
        })}
                            </div>
                            <div class="system-title-area paddingBottom24Pixel">
                                <sh-text size="title-1">Robotic Networks</sh-text>
                            </div>
                            <div class="robotic-networks">
                                ${this.associatedRoboticNetworkSummaries.length > 0 ?
            (_c = this.associatedRoboticNetworkSummaries) === null || _c === void 0 ? void 0 : _c.map((rnSummary) => html `
                                    <div>
                                    <sh-text size="body-1">${rnSummary.roboticNetworkName}</sh-text>
                                    </div>`) :
            html `<sh-text size="body-1">(none)</sh-text>`}
                            </div>
                        </div>
                    ` : html ``}
                </div>
                <div id="CardFooter" slot="footer">
                    <div id="LeftButtons">
                        <div id="SystemLeftButtons">
                            <div id="InfoIconAndText">
                                ${self.adding ?
            html `
                                            <sh-icon icon="information" color="rgb(var(--ui-0))"></sh-icon>
                                            <sh-text>Saving will generate a new configuration file that will automatically
                                                download.
                                            </sh-text>` : html ``}
                            </div>
                            ${self.viewing && (!self.isSystemAssociated || !self.showAssociatedSystem) ?
            html `
                                        <div>
                                            <sh-button label='Delete System' color="tertiary" size="l"
                                                       ?disabled=${self.isSystemAssociated}
                                                       @click=${self.initiateDelete}>
                                            </sh-button>
                                        </div>` : html ``}
                        </div>
                    </div>
                    <div id="RightButtons">
                        ${self.viewing ?
            html `
                                    <sh-button label='New Password' color="secondary" size="l"
                                               @click=${self.initiateGenerateNewPassword}>
                                    </sh-button>
                                ` : html ``}
                        ${self.viewing && self.isSystemAssociated ?
            html `
                                    <sh-button
                                            label=${this.showAssociatedSystem ? 'Hide Association' : 'Show Association'}
                                            size="l"
                                            @click=${self.toggleShowAssociatedSystem} color="secondary">
                                    </sh-button>
                                ` : html ``}
                        ${self.adding || self.editing ?
            html `
                                    <sh-button label='Cancel'
                                               @click=${self.cancelAddOrEdit} color="secondary">
                                    </sh-button>
                                    <sh-button id='Save' label='Save'
                                               @click=${self.save}
                                               ?disabled=${self.validationTracker.isSaveNotAllowed(self.editing)}>
                                    </sh-button>` : html ``}
                        ${self.viewing ?
            html `
                                    <sh-button label='Edit'
                                               ?disabled=${self.isSystemAssociated}
                                               @click=${self.initiateEdit}>
                                    </sh-button>` : html ``}
                    </div>
                    </div>
                    <sh-modal label='New Password' 
                             sticky
                             modal-height="220px"
                             modal-width="550px"
                             type="alert"
                             ?visible=${this.generatingNewPassword}>
                       <div id="NewPasswordModalContent">
                           <sh-text>Press Continue to generate a new password for this system and 
                               download a configuration file that contains it.</sh-text>
                           <sh-text>Press Cancel to leave the password unchanged.</sh-text>
                       </div>
                       <sh-button slot="footer" color="secondary" label="Cancel" 
                              @click=${self.cancelGenerateNewPassword}></sh-button>
                       <sh-button slot="footer" color="primary" label="Continue" size="l" 
                              @click=${this.generateNewPassword}></sh-button>
                    </sh-modal>
                ${self.deleteItemModalHtml}
            </sh-card>`;
    }
};
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "status", void 0);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "systemName", null);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "systemIdInternal", null);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "systemState", null);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "associationState", null);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "systemCreated", null);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "systemSide", null);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "hospitalName", null);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "address", null);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "version", null);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "systemIsEnabled", null);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "associatedSystem", void 0);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "roboticNetworkSummaries", void 0);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "associatedRoboticNetworkSummaries", void 0);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "isSystemAssociated", null);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "showAssociatedSystem", void 0);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "systemsLastReport", null);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "associatedSystemsLastReport", null);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "systemsOnlineStatus", null);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "associatedSystemsOnlineStatus", null);
__decorate([
    internalProperty()
], CorPageSystemsCard.prototype, "generatingNewPassword", void 0);
CorPageSystemsCard = CorPageSystemsCard_1 = __decorate([
    customElement('cor-page-systems-card')
], CorPageSystemsCard);
export { CorPageSystemsCard };
//# sourceMappingURL=CorPageSystems.js.map